import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Crown, ExternalLink, Loader2, ArrowLeft, Calendar, CreditCard, Package, Sparkles } from "lucide-react";
import { useDemoMode } from "@/lib/demo-mode";
import { useAuth } from "@/hooks/use-auth";
import { useAdmin } from "@/hooks/use-admin";
import { planCopy } from "@/lib/plan-copy";
import { useCommercialConfig } from "@/lib/pricing-labels";
import { usePremium } from "@/lib/premium";
import { createPortalSession } from "@/utils/payments.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { storePostLoginRedirect } from "@/lib/auth-redirect";
import { ShieldCheck } from "lucide-react";
import { getMyPremiumUsage, getMyCredits, type PremiumUsageStats } from "@/lib/quota.functions";
import { usePricingLabels } from "@/lib/pricing-labels";
import { planLabelForPriceId } from "@/lib/commercial-copy";
import { syncMySubscription } from "@/lib/subscription-sync.functions";
import { deleteMyAccount } from "@/lib/account-deletion.functions";
import { ACCOUNT_DELETION_CONFIRMATION, isDeletionConfirmed } from "@/lib/account-deletion";
import { supabase } from "@/integrations/supabase/client";
import { Trash2, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/account")({ component: AccountPage });

const BILLING_PORTAL_OPENED_AT_KEY = "routify:billing-portal-opened-at";
const BILLING_PORTAL_RETURN_WINDOW_MS = 15 * 60 * 1000;

function isStandalonePwa() {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia("(display-mode: standalone)").matches || nav.standalone === true;
}

function markBillingPortalOpened() {
  try {
    localStorage.setItem(BILLING_PORTAL_OPENED_AT_KEY, String(Date.now()));
  } catch {}
}

function getBillingPortalOpenedAt() {
  try {
    const value = Number(localStorage.getItem(BILLING_PORTAL_OPENED_AT_KEY));
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

function clearBillingPortalMarker() {
  try {
    localStorage.removeItem(BILLING_PORTAL_OPENED_AT_KEY);
  } catch {}
}

function shouldCloseBillingPortalReturnTab(returnTimestamp?: number) {
  if (typeof window === "undefined") return false;
  const openedAt = returnTimestamp && Number.isFinite(returnTimestamp) ? returnTimestamp : getBillingPortalOpenedAt();
  const recentlyOpened = openedAt > 0 && Date.now() - openedAt < BILLING_PORTAL_RETURN_WINDOW_MS;
  const mobileBrowserShell = /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);

  return !isStandalonePwa() && (Boolean(window.opener) || (recentlyOpened && mobileBrowserShell));
}

function logBillingPortal(event: string, details?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  console.info(`[billing-portal] ${event}`, {
    standalone: isStandalonePwa(),
    visibility: document.visibilityState,
    ...details,
  });
}

function AccountPage() {
  const { available: demoAvailable, enabled: demoEnabled, setEnabled: setDemoEnabled } = useDemoMode();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isPremium, subscription, loading, refetch: refetchPremium } = usePremium();
  const { isAdmin, isOwner } = useAdmin();
  const pricingLabels = usePricingLabels();
  const commercialConfig = useCommercialConfig();
  const navigate = useNavigate();
  const portalFn = useServerFn(createPortalSession);
  const usageFn = useServerFn(getMyPremiumUsage);
  const creditsFn = useServerFn(getMyCredits);
  const syncFn = useServerFn(syncMySubscription);
  const [opening, setOpening] = useState(false);
  const deleteAccountFn = useServerFn(deleteMyAccount);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [portalSyncActive, setPortalSyncActive] = useState(false);
  // Cache scoped per user: a shared key leaked the previous account's plan
  // into a freshly created account on the same browser.
  const usageCacheKey = user ? `routify:premium-usage:${user.id}` : null;
  const [usage, setUsage] = useState<PremiumUsageStats | null>(() => {
    if (typeof window === "undefined" || !usageCacheKey) return null;
    try {
      const raw = localStorage.getItem(usageCacheKey);
      return raw ? (JSON.parse(raw) as PremiumUsageStats) : null;
    } catch {
      return null;
    }
  });

  const [credits, setCredits] = useState<number>(0);
  const [usageLoading, setUsageLoading] = useState(() => Boolean(isAuthenticated));

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      storePostLoginRedirect();
      navigate({ to: "/login" });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) {
      setUsage(null);
      setCredits(0);
      setUsageLoading(false);
      return;
    }

    // Never keep another account's cached numbers on screen.
    setUsage(usageCacheKey ? (() => {
      try {
        const raw = localStorage.getItem(usageCacheKey);
        return raw ? (JSON.parse(raw) as PremiumUsageStats) : null;
      } catch { return null; }
    })() : null);

    try { localStorage.removeItem("routify:premium-usage"); } catch {}

    setUsageLoading(true);
    usageFn({})
      .then((result) => {
        if (cancelled) return;
        setUsage(result);
        try { if (usageCacheKey) localStorage.setItem(usageCacheKey, JSON.stringify(result)); } catch {}
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      })
      .finally(() => {
        if (!cancelled) setUsageLoading(false);
      });

    creditsFn({}).then((r) => !cancelled && setCredits(r.balance)).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, usageFn, creditsFn, usageCacheKey]);


  const syncSubscriptionNow = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    const environment = getStripeEnvironment();
    await syncFn({ data: { environment } });
    // A sincronização atualiza apenas a fonte de pagamento. O plano visível é
    // sempre decidido pelo resolvedor central, que também considera ofertas,
    // papéis Pro e subscrições dos dois ambientes. Gravar diretamente o último
    // registo Stripe aqui fazia uma subscrição inativa/antiga trocar Premium por
    // Livre até a verificação autoritativa terminar.
    await refetchPremium();
  }, [isAuthenticated, refetchPremium, syncFn, user]);


  // Safety net for missed Stripe webhooks: reconcile the DB with Stripe
  // on account page open, and again when the tab regains focus (e.g. user
  // just came back from the billing portal in another tab).
  useEffect(() => {
    if (!isAuthenticated) return;
    const reconcile = () => {
      syncSubscriptionNow()
        .catch(() => {});
    };
    reconcile();
    const onFocus = () => { if (document.visibilityState === "visible") reconcile(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAuthenticated, syncSubscriptionNow]);

  // While the Billing Portal is open in another tab/app, poll briefly so a
  // cancellation is reflected automatically even if the browser/PWA does not
  // fire a reliable focus/visibility event on Android.
  useEffect(() => {
    if (!portalSyncActive) return;
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      if (Date.now() - startedAt > 10 * 60 * 1000) {
        setPortalSyncActive(false);
        return;
      }
      syncSubscriptionNow().catch(() => {});
    }, 5_000);
    syncSubscriptionNow().catch(() => {});
    return () => window.clearInterval(interval);
  }, [portalSyncActive, syncSubscriptionNow]);

  // When Stripe sends the user back to the app/site, Android can open it as a
  // browser tab instead of restoring the installed PWA. Use the return marker
  // to force an immediate reconciliation in that fresh context too.
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing") !== "return") return;

    const returnTimestamp = Number(params.get("t"));
    const shouldAutoClose = shouldCloseBillingPortalReturnTab(returnTimestamp);
    logBillingPortal("return detected", { shouldAutoClose, returnTimestamp });

    // Regresso do Stripe: revalida já, mas sem apagar a cache (isso fazia o
    // Premium desaparecer durante alguns segundos).
    setPortalSyncActive(true);

    params.delete("billing");
    params.delete("t");
    const nextSearch = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`,
    );

    let cancelled = false;
    syncSubscriptionNow()
      .catch((error) => logBillingPortal("return sync failed", { error: error instanceof Error ? error.message : "unknown" }))
      .finally(() => {
        if (cancelled) return;
        clearBillingPortalMarker();
        if (!shouldAutoClose) return;

        window.setTimeout(() => {
          logBillingPortal("closing return tab");
          window.close();
          window.setTimeout(() => {
            if (!document.hidden) logBillingPortal("return tab close was blocked by browser");
          }, 800);
        }, 500);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, syncSubscriptionNow, user]);

  async function handleDeleteAccount() {
    if (!isDeletionConfirmed(deleteText)) return;
    setDeleting(true);
    try {
      const result = await deleteAccountFn({ data: { confirmation: deleteText.trim().toUpperCase() } });
      if (!result.ok) throw new Error(result.error);
      toast.success("Conta eliminada. Até breve.");
      try { localStorage.clear(); } catch {}
      await supabase.auth.signOut();
      window.location.replace("/");
    } catch (e) {
      toast.error((e as Error).message || "Não foi possível eliminar a conta");
      setDeleting(false);
    }
  }

  async function openPortal() {
    setOpening(true);
    try {
      const r = await portalFn({
        data: {
          environment: getStripeEnvironment(),
          returnUrl: `${window.location.origin}/account?billing=return&t=${Date.now()}`,
        },
      });
      if ("error" in r) throw new Error(r.error);
      markBillingPortalOpened();

      // On the installed PWA (Android/iOS), opening in a new tab launches a
      // Chrome Custom Tab / in-app browser that never returns to the PWA —
      // the user ends up stuck in a browser shell with the URL bar on top.
      // Navigate the PWA window itself so returning from Stripe stays
      // inside the installed app.
      if (isStandalonePwa()) {
        logBillingPortal("opening portal", { target: "self-standalone" });
        setPortalSyncActive(true);
        window.location.assign(r.url);
        return;
      }

      logBillingPortal("opening portal", { target: "blank" });
      const portalWindow = window.open(r.url, "_blank");
      if (!portalWindow) {
        logBillingPortal("popup blocked, navigating current window");
        window.location.assign(r.url);
        return;
      }
      setPortalSyncActive(true);
    } catch (e) {
      toast.error((e as Error).message || "Não foi possível abrir o portal");
    } finally {
      setOpening(false);
    }
  }

  const subscriptionWillEnd = Boolean(subscription && (subscription.cancel_at_period_end || subscription.status === "canceled"));
  const scheduledEnd = subscription?.cancel_at ?? subscription?.current_period_end ?? null;
  const endDate = (subscriptionWillEnd ? scheduledEnd : subscription?.current_period_end)
    ? new Date((subscriptionWillEnd ? scheduledEnd : subscription?.current_period_end) as string)
    : null;
  const planLabel = planLabelForPriceId(subscription?.price_id, pricingLabels);
  const subscriptionEnded = Boolean(
    subscription && !isPremium && (subscription.status === "canceled" || (endDate && endDate.getTime() <= Date.now())),
  );

  if (authLoading) {
    return (
      <main className="mx-auto min-h-[70vh] w-full max-w-2xl px-4 py-8">
        <Link to="/" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Link>
        <div className="h-8 w-48 rounded bg-muted" aria-hidden="true" />
        <div className="mt-2 h-4 w-64 rounded bg-muted" aria-hidden="true" />
        <section className="mt-6 min-h-[240px] rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)]" aria-busy="true">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> A carregar conta…
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-[70vh] w-full max-w-2xl px-4 py-8">
      <Link to="/" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>
      <h1 className="text-2xl font-bold tracking-tight">A minha conta</h1>
      <p className="mt-1 text-sm text-muted-foreground">{user?.email}</p>

      <div className="mt-4 flex flex-wrap gap-2 min-h-[42px]">
        {isAdmin ? (
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/5 px-4 py-2 text-sm font-medium text-brand hover:bg-brand/10"
          >
            <ShieldCheck className="h-4 w-4" /> Painel de administração
          </Link>
        ) : null}
        {isOwner ? (
          <Link
            to="/owner"
            className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/5 px-4 py-2 text-sm font-medium text-amber-600 hover:bg-amber-500/10 dark:text-amber-400"
          >
            <Crown className="h-4 w-4" /> Painel do Fundador
          </Link>
        ) : null}
      </div>


      <section className="mt-6 min-h-[240px] rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)]">
        <div className="flex items-center gap-2">
          <Crown className={`h-5 w-5 ${isPremium ? "text-brand" : "text-muted-foreground"}`} />
          <h2 className="text-base font-semibold">Subscrição</h2>
        </div>

        {loading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> A carregar…
          </div>
        ) : isPremium ? (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">
                <Crown className="h-3 w-3" /> Premium ativo
              </span>
              {subscription?.status === "trialing" && (
                <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
                  Período de teste
                </span>
              )}
              {subscriptionWillEnd && (
                <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-900">
                  Cancelamento agendado{endDate ? ` — Premium ativo até ${endDate.toLocaleDateString()}` : ""}
                </span>
              )}
            </div>
            <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
              <div className="inline-flex items-center gap-2"><CreditCard className="h-4 w-4" /> Plano: <span className="font-medium text-foreground">{subscription ? planLabel : "Premium manual"}</span></div>
              {endDate && (
                <div className="inline-flex items-center gap-2"><Calendar className="h-4 w-4" /> {subscriptionWillEnd ? "Ativo até" : "Renova em"}: <span className="font-medium text-foreground">{endDate.toLocaleDateString()}</span></div>
              )}
            </div>
            {subscription && (
              <button
                type="button"
                onClick={openPortal}
                disabled={opening}
                className="mt-2 inline-flex items-center gap-2 rounded-full border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-60"
              >
                {opening ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                {subscriptionWillEnd ? "Reativar subscrição" : "Cancelar ou gerir subscrição"}
              </button>
            )}
            <p className="text-xs text-muted-foreground">
              {!subscription
                ? "O teu acesso Premium foi ativado manualmente pela equipa Routify."
                : subscriptionWillEnd
                  ? "O cancelamento já está registado. Mantens o acesso até ao fim do período pago; podes reativar no portal Stripe."
                  : "Atualiza método de pagamento, vê faturas ou cancela a subscrição. O acesso mantém-se até ao fim do período pago."}
            </p>

          </div>
        ) : (
          <div className="mt-4">
            {subscriptionEnded && (
              <span className="mb-3 inline-flex rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
                Subscrição cancelada
              </span>
            )}
            <p className="text-sm text-muted-foreground">
              {planCopy("pt").upsellPremium(commercialConfig.limits)}
            </p>

            <a
              href="/?premium=1"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Crown className="h-4 w-4" /> Ver Premium
            </a>
          </div>
        )}
      </section>

      {isPremium && usageLoading && !usage && (
        <section className="mt-4 min-h-[112px] rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)]" aria-busy="true">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 animate-pulse rounded-full bg-muted" />
            <div className="h-5 w-40 animate-pulse rounded bg-muted" />
          </div>
          <div className="mt-4 h-3 w-full animate-pulse rounded bg-muted" />
          <div className="mt-2 h-3 w-3/4 animate-pulse rounded bg-muted" />
        </section>
      )}

      {isPremium && usage && !usage.grandfathered && (
        <section className="mt-4 rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)]">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand" />
            <h2 className="text-base font-semibold">Utilização este mês</h2>
          </div>
          <div className="mt-4 space-y-3">
            <div>
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-muted-foreground">Rotas IA criadas (últimos 30 dias)</span>
                <span className="font-semibold">
                  {Math.min(usage.usedMonth, usage.monthlyLimit)}/{usage.monthlyLimit}
                </span>
              </div>
              <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-brand transition-all"
                  style={{ width: `${Math.min(100, (usage.usedMonth / usage.monthlyLimit) * 100)}%` }}
                />
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                Hoje: {usage.usedToday}/{usage.dailyLimit} · O contador renova ao longo dos próximos 30 dias.
              </p>
            </div>
            {usage.usedMonth >= usage.monthlyLimit * 0.7 && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
                A aproximares-te do limite mensal? Compra um pack de rotas — não expira e soma-se ao Premium.
              </p>
            )}
          </div>
        </section>
      )}

      {isPremium && usage?.grandfathered && (
        <section className="mt-4 rounded-2xl border border-brand/40 bg-brand/5 p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-brand">
            <Crown className="h-4 w-4" /> Premium fundador — rotas ilimitadas
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            És dos primeiros subscritores: manténs acesso ilimitado para sempre, como agradecimento por nos teres apoiado no início.
          </p>
        </section>
      )}

      <section className="mt-4 rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)]">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-foreground" />
          <h2 className="text-base font-semibold">Packs de rotas</h2>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Compra packs de rotas avulso. Nunca expiram, somam-se ao teu plano e são perfeitos para uma viagem pontual.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <a
            href="/credits"
            className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background hover:opacity-90"
          >
            <Package className="h-4 w-4" /> Comprar packs
          </a>
          <span className="text-xs text-muted-foreground">Saldo atual: <strong className="text-foreground">{credits}</strong> rotas</span>
        </div>
      </section>

      {demoAvailable && (
        <section className="mt-4 rounded-2xl border border-amber-400/60 bg-amber-50 p-5 dark:bg-amber-950/30">
          <h2 className="text-base font-semibold">Modo de teste (build interna)</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Percorre Premium e Pro no telemóvel sem gastar créditos e sem chamadas pagas de IA ou Google Maps.
            Os roteiros são fixos e locais; nada é guardado nem cobrado.
          </p>
          <button
            type="button"
            onClick={() => setDemoEnabled(!demoEnabled)}
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            {demoEnabled ? "Desativar modo de teste" : "Ativar modo de teste"}
          </button>
          {demoEnabled && (
            <p className="mt-2 text-xs font-semibold text-amber-800 dark:text-amber-200">
              Modo de teste ATIVO — dados de demonstração.
            </p>
          )}
        </section>
      )}

      <section className="mt-4 rounded-2xl border border-destructive/40 bg-destructive/5 p-5">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <h2 className="text-base font-semibold text-destructive">Eliminar conta</h2>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Apaga definitivamente a tua conta, rotas guardadas, lugares e preferências. As subscrições ativas são
          canceladas. Registos exigidos por lei (faturação) são conservados de forma anonimizada. Esta ação é
          irreversível.
        </p>
        {!deleteOpen ? (
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-destructive/50 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" /> Eliminar a minha conta
          </button>
        ) : (
          <div className="mt-4 space-y-3">
            <label className="block text-sm font-medium">
              Escreve <strong>{ACCOUNT_DELETION_CONFIRMATION}</strong> para confirmar
              <input
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                autoComplete="off"
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                placeholder={ACCOUNT_DELETION_CONFIRMATION}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!isDeletionConfirmed(deleteText) || deleting}
                onClick={handleDeleteAccount}
                className="inline-flex items-center gap-2 rounded-full bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:opacity-90 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Eliminar definitivamente
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => { setDeleteOpen(false); setDeleteText(""); }}
                className="inline-flex items-center rounded-full border border-input px-4 py-2 text-sm font-medium hover:bg-accent"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
